import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  X, ChevronRight, ChevronDown, CheckCircle2, Circle, Loader2, ExternalLink, Clock, FileText, Image as ImageIcon
} from 'lucide-react';
import { leadDetail360API } from '../api';

const fmt = (d, p = 'd MMM yyyy') => { try { return d ? format(new Date(d), p) : '—'; } catch { return '—'; } };

const DONE = ['APPROVED', 'IFC_RELEASED', 'COMPLETED'];
const ACTIVE = ['IN_PROGRESS', 'INTERNAL_REVIEW', 'CLIENT_APPROVAL_PENDING', 'WAITING_FOR_CLIENT'];

const statusLabel = (s) => (s || 'NOT_STARTED').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const StageIcon = ({ status }) => {
  if (DONE.includes(status)) return <CheckCircle2 className="h-5 w-5 text-emerald-500" data-testid="stage-icon-done" />;
  if (ACTIVE.includes(status)) return <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-blue-500"><span className="h-2 w-2 rounded-full bg-blue-500" /></span>;
  return <Circle className="h-5 w-5 text-slate-300" />;
};

const badgeClass = (status) => {
  if (DONE.includes(status)) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (status === 'IN_PROGRESS') return 'bg-blue-50 text-blue-700 border-blue-200';
  if (ACTIVE.includes(status)) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-slate-50 text-slate-500 border-slate-200';
};

export const ProjectWorkflowDrawer = ({ leadId, open, onClose }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('workflow');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leadDetail360API.workflow(leadId);
      setData(res.data);
    } catch (e) {
      setData({ error: e.response?.data?.detail || 'Unable to load workflow' });
    } finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const h = data?.header || {};
  const c = data?.counts || {};
  const stages = data?.stages || [];
  const tabs = [['workflow', 'Workflow'], ['tasks', 'Tasks'], ['drawings', 'Drawings'], ['files', 'Files']];

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="workflow-drawer-overlay">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <aside
        data-testid="project-workflow-drawer"
        className="flex h-full w-full max-w-[380px] flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">Project Work Flow</h2>
          <button onClick={onClose} data-testid="workflow-drawer-close" className="text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading && <div className="flex flex-1 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>}
        {!loading && data?.error && <p className="p-4 text-sm text-red-600" data-testid="workflow-drawer-error">{data.error}</p>}

        {!loading && !data?.error && (
          <>
            <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
              <div className="flex items-start gap-3">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 text-amber-700">
                  <ImageIcon className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-50" data-testid="drawer-project-name">{h.project_name || '—'}</p>
                  <p className="text-xs text-slate-500">{h.project_id || '—'}</p>
                  <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{statusLabel(h.status)}
                  </span>
                </div>
                <div className="w-24 shrink-0 text-right">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">Progress</p>
                  <p className="text-sm font-bold text-slate-900 dark:text-slate-50">{h.progress ?? 0}%</p>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${h.progress ?? 0}%` }} />
                  </div>
                  <p className="mt-1 text-[10px] text-slate-500">{c.completed ?? 0}/{c.total ?? 0} Completed</p>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500" data-testid="drawer-summary-stats">
                <span data-testid="drawer-project-age"><Clock className="mr-1 inline h-3 w-3" />Project Age: <b className="text-slate-700 dark:text-slate-200">{h.project_age?.label || '—'}</b>{h.project_age?.completed ? ' (final)' : ''}</span>
                <span>{c.in_progress ?? 0} In Progress</span>
                <span className={c.overdue ? 'text-red-600' : ''}>{c.overdue ?? 0} Overdue</span>
              </div>
            </div>

            <div className="flex gap-4 border-b border-slate-100 px-4 dark:border-slate-800">
              {tabs.map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  data-testid={`drawer-tab-${k}`}
                  className={`border-b-2 py-2 text-xs font-medium transition-colors ${tab === k ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3">
              {tab === 'workflow' && (
                stages.length === 0
                  ? <p className="py-8 text-center text-sm text-slate-500" data-testid="drawer-no-stages">No workflow stages configured for this project's services yet.</p>
                  : <ol className="space-y-1" data-testid="drawer-stage-list">
                    {stages.map((s, i) => {
                      const isOpen = expanded === s.id;
                      return (
                        <li key={s.id} className="relative pl-8" data-testid={`drawer-stage-${i + 1}`}>
                          {i < stages.length - 1 && <span className="absolute left-[9px] top-6 h-full w-px bg-slate-200 dark:bg-slate-700" />}
                          <span className="absolute left-0 top-1"><StageIcon status={s.status} /></span>
                          <button onClick={() => setExpanded(isOpen ? null : s.id)} className="flex w-full items-start gap-2 py-1 text-left" data-testid={`drawer-stage-toggle-${i + 1}`}>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">{i + 1}. {s.name}</span>
                              <span className={`mt-0.5 inline-block rounded border px-1.5 py-0.5 text-[10px] font-medium ${badgeClass(s.status)}`}>{statusLabel(s.status)}</span>
                              <span className="mt-1 block text-[11px] text-slate-500">{fmt(s.start_date)} – {fmt(s.due_date)}</span>
                              {s.assignee && <span className="block text-[11px] text-slate-500">{s.assignee}</span>}
                            </span>
                            {isOpen ? <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-slate-400" /> : <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-400" />}
                          </button>
                          {isOpen && (
                            <div className="mb-2 mt-1 space-y-1.5 rounded-lg bg-slate-50 p-2.5 text-[11px] dark:bg-slate-800/60" data-testid={`drawer-stage-detail-${i + 1}`}>
                              <p className="flex justify-between"><span className="text-slate-500">Current revision</span><b>{s.current_revision || '—'}</b></p>
                              <p className="flex justify-between"><span className="text-slate-500">Revisions</span><b>{s.revision_count ?? 0}</b></p>
                              <p className="flex justify-between"><span className="text-slate-500">Internal review</span><b>{s.requires_internal_review ? 'Required' : 'Not required'}</b></p>
                              <p className="flex justify-between"><span className="text-slate-500">Client approval</span><b>{s.requires_client_approval ? 'Required' : 'Not required'}</b></p>
                              <p className="flex justify-between"><span className="text-slate-500">IFC release</span><b>{s.requires_ifc ? 'Required' : 'Not required'}</b></p>
                              <p className="flex justify-between"><span className="text-slate-500">{s.overdue_days ? 'Overdue by' : 'Days remaining'}</span>
                                <b className={s.overdue_days ? 'text-red-600' : ''}>{s.overdue_days ? `${s.overdue_days} days` : (s.days_remaining ?? '—')}</b></p>
                              <div className="pt-1">
                                <p className="mb-1 text-slate-500">Drawings in this stage</p>
                                {(data.drawings || []).filter((d) => d.deliverable_id === s.id).slice(0, 4).map((d) => (
                                  <p key={d.id} className="flex items-center gap-1 truncate"><FileText className="h-3 w-3 text-slate-400" />{d.revision_number} · {d.filename || 'file'}</p>
                                ))}
                                {(data.drawings || []).filter((d) => d.deliverable_id === s.id).length === 0 && <p className="text-slate-400">None uploaded</p>}
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
              )}

              {tab === 'tasks' && (
                (data.tasks || []).length === 0
                  ? <p className="py-8 text-center text-sm text-slate-500">No project tasks yet.</p>
                  : <ul className="space-y-2" data-testid="drawer-task-list">
                    {data.tasks.map((t) => (
                      <li key={t.id} className="rounded-lg border border-slate-200 p-2 text-xs dark:border-slate-700">
                        <p className="font-medium text-slate-800 dark:text-slate-100">{t.title}</p>
                        <p className="mt-0.5 text-slate-500">{statusLabel(t.status)} · {fmt(t.due_date)}{t.assignee ? ` · ${t.assignee}` : ''}</p>
                      </li>
                    ))}
                  </ul>
              )}

              {tab === 'drawings' && (
                (data.drawings || []).length === 0
                  ? <p className="py-8 text-center text-sm text-slate-500">No drawings uploaded yet.</p>
                  : <ul className="space-y-2" data-testid="drawer-drawing-list">
                    {data.drawings.map((d) => (
                      <li key={d.id} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2 text-xs dark:border-slate-700">
                        <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                        <span className="min-w-0 flex-1 truncate">{d.revision_number} · {d.filename || 'drawing'}</span>
                        <span className="shrink-0 text-slate-400">{fmt(d.created_at, 'd MMM')}</span>
                      </li>
                    ))}
                  </ul>
              )}

              {tab === 'files' && (
                (data.files || []).length === 0
                  ? <p className="py-8 text-center text-sm text-slate-500">No project files yet.</p>
                  : <ul className="space-y-2" data-testid="drawer-file-list">
                    {data.files.map((f) => (
                      <li key={f.id} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2 text-xs dark:border-slate-700">
                        <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                        <span className="min-w-0 flex-1 truncate">{f.name}</span>
                        <span className="shrink-0 text-slate-400">{f.category || ''}</span>
                      </li>
                    ))}
                  </ul>
              )}
            </div>

            <div className="border-t border-slate-100 p-3 dark:border-slate-800">
              <button
                onClick={() => navigate(`/architect/projects/${leadId}`)}
                data-testid="drawer-open-full-project"
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
              >
                Open Full Project <ExternalLink className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
};

export default ProjectWorkflowDrawer;
